import React, { FunctionComponent, ReactNode, useState } from 'react'

import { useField } from 'formik'
import { NumberFormatValues, NumericFormatProps } from 'react-number-format'
import { SpaceProps, WidthProps } from 'styled-system'

import { NumericInput } from '@/atoms/input'
import { getCurrencySymbol } from '@/helpers/number'
import { isNil } from '@/helpers/type-guards'
import { AdornmentPosition, Currency } from '@/models/common'

import FieldWrapper from './field-wrapper'

interface OwnProps {
  label?: string
  name?: string
  decimalScale?: number
  fieldProps?: SpaceProps & WidthProps
  onChange?: (val: NumberFormatValues) => void
  onBlur?: () => void
  isDisabled?: boolean
  hideErrorMessage?: boolean
  currency?: Currency
  adornmentPosition?: AdornmentPosition
  adornment?: ReactNode
  tooltip?: string
}

export type CurrencyInputProps = Omit<OwnProps & NumericFormatProps, 'onChange'> & {
  onChange?: (val: NumberFormatValues) => void
}

const CurrencyInputField: FunctionComponent<CurrencyInputProps> = ({
  label,
  currency,
  fieldProps,
  name,
  onChange,
  hideErrorMessage,
  onBlur,
  decimalScale,
  tooltip,
  fieldValue: _fieldValue,
  valueFormat: _valueFormat,
  ...rest
}: CurrencyInputProps & { fieldValue?: unknown; valueFormat?: unknown }) => {
  const [field, meta, helpers] = useField(name)
  const [floatValue, setFloatValue] = useState<number>()
  const hasError: boolean = meta.touched && !!meta.error

  const handleInputChange = (values: NumberFormatValues) => {
    const floatValue = isNil(values?.floatValue) ? '' : values.floatValue
    helpers && helpers.setValue(floatValue)
    setFloatValue(values.floatValue)

    onChange && onChange(values)
  }
  const handleInputBlur = (): void => {
    helpers && helpers.setTouched(true)
    onBlur && onBlur()
  }

  return (
    <FieldWrapper id={field.name} meta={meta} label={label} fieldProps={fieldProps} hideErrorMessage={hideErrorMessage} tooltip={tooltip}>
      <NumericInput
        id={field.name}
        hasError={hasError}
        name={field.name}
        value={field.value}
        onValueChange={handleInputChange}
        onBlur={handleInputBlur}
        floatvalue={floatValue}
        decimalScale={decimalScale ?? 2}
        thousandSeparator=","
        decimalSeparator="."
        prefix={getCurrencySymbol(currency)}
        placeholder={`${getCurrencySymbol(currency)}10,000.00`}
        {...rest}
      />
    </FieldWrapper>
  )
}

CurrencyInputField.displayName = 'CurrencyInputField'

export default CurrencyInputField
